import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import ArrowOutward from "~icons/material-symbols/arrow-outward";
import ChevronRight from "~icons/material-symbols/chevron-right";
import { css, cx } from "../../../styled-system/css";
import Header from "../../components/header";
import { getCreditCardsOrEmpty } from "../../features/charge/credit-card-loader";
import type { WalletClient } from "../../libs/client";
import type { TenantWithFeature } from "../../libs/connect";
import { ERROR_CODES, ERROR_MESSAGES } from "../../libs/errors";
import { t } from "../../libs/i18n";
import {
  type SettingsProfile,
  getSettingsProfile,
  openMiniAppServiceSettings,
} from "../../libs/sdk";
import { InfoDisplayToggle } from "./info-display-toggle";

const INFO_DISPLAY_STORAGE_KEY = "settings-info-display";

// localStorage は破損値もありうるため、明示的に "false" のときだけ非表示にする
function readInfoDisplay(): boolean {
  return localStorage.getItem(INFO_DISPLAY_STORAGE_KEY) !== "false";
}

const pageCss = css({
  px: "20px",
  pt: "24px",
  pb: "104px",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

const sectionCss = css({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const sectionLabelCss = css({
  fontSize: "14px",
  lineHeight: "1.4",
  fontWeight: "600",
  color: "text.tertiary",
});

const panelCss = css({
  bg: "surface.primary",
  border: "1px solid",
  borderColor: "border.secondary",
  rounded: "16px",
  overflow: "hidden",
});

// 情報行: 左端は 16px パディング、区切り線は内側コンテンツに付けて左 16px インセットにする
const rowCss = css({
  w: "full",
  pl: "16px",
  display: "flex",
  alignItems: "center",
});

const rowContentCss = css({
  flex: 1,
  minW: 0,
  py: "16px",
  pr: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
});

const rowDividerCss = css({
  borderTop: "1px solid",
  borderColor: "border.secondary",
});

const linkRowCss = css({
  w: "full",
  pl: "16px",
  pr: "12px",
  py: "16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "16px",
  bg: "surface.primary",
  color: "text.primary",
  textAlign: "left",
  cursor: "pointer",
});

const rowLabelCss = css({
  fontSize: "16px",
  lineHeight: "1.5",
  color: "text.primary",
  flexShrink: 0,
});

const rowValueCss = css({
  fontSize: "14px",
  lineHeight: "1.4",
  color: "text.secondary",
  textAlign: "right",
  wordBreak: "break-all",
});

const maskedValueCss = css({
  w: "160px",
  h: "20px",
  rounded: "4px",
  bg: "surface.disable",
});

const rowIconCss = css({
  color: "text.tertiary",
  flexShrink: 0,
  fontSize: "24px",
});

type ProfileField = {
  label: string;
  key: keyof SettingsProfile;
};

const profileFields: ProfileField[] = [
  { label: t("氏名"), key: "name" },
  { label: t("住所"), key: "address" },
  { label: t("性別"), key: "gender" },
  { label: t("生年月日"), key: "birthday" },
];

type Props = {
  client: WalletClient;
  tenant: TenantWithFeature;
};

export function SettingsScreen({ client, tenant }: Props) {
  const navigate = useNavigate();
  const [infoDisplay, setInfoDisplay] = useState<boolean>(readInfoDisplay);

  const { data: profile, error } = useQuery({
    queryKey: ["settings-profile"],
    queryFn: getSettingsProfile,
    // 取得失敗は画面全体をエラーページに落とさず、トーストで通知するに留める
    throwOnError: false,
    meta: { errorCode: ERROR_CODES.PROFILE_READ_FAILED },
  });

  useEffect(() => {
    if (error) {
      toast.error(ERROR_MESSAGES[ERROR_CODES.PROFILE_READ_FAILED]);
    }
  }, [error]);

  // 既存カードの削除導線としてのセクションのため、0枚のときは表示しない
  const { data: creditCards } = useQuery({
    queryKey: ["settings-credit-cards"],
    queryFn: () => getCreditCardsOrEmpty(client),
    enabled: tenant.feature.charge,
  });
  const hasCreditCards =
    Object.keys(creditCards?.response.cards ?? {}).length > 0;

  const toggleInfoDisplay = useCallback((next: boolean) => {
    setInfoDisplay(next);
    localStorage.setItem(INFO_DISPLAY_STORAGE_KEY, String(next));
  }, []);

  const openMiniAppDetail = useCallback(() => {
    void openMiniAppServiceSettings();
  }, []);

  return (
    <>
      <Header title={t("設定")} />
      <main className={pageCss}>
        <section className={sectionCss}>
          <div
            className={css({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            })}
          >
            <h2 className={sectionLabelCss}>
              {t("ミニアプリで利用するじぶんの情報")}
            </h2>
            <InfoDisplayToggle
              checked={infoDisplay}
              onChange={toggleInfoDisplay}
            />
          </div>
          <div className={panelCss}>
            {profileFields.map((field, index) => (
              <div key={field.key} className={rowCss}>
                <div
                  className={
                    index === 0
                      ? rowContentCss
                      : cx(rowContentCss, rowDividerCss)
                  }
                >
                  <span className={rowLabelCss}>{field.label}</span>
                  {infoDisplay ? (
                    <span className={rowValueCss}>{profile?.[field.key]}</span>
                  ) : (
                    <span className={maskedValueCss} aria-hidden />
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={sectionCss}>
          <h2 className={sectionLabelCss}>
            {t("ミニアプリ情報（お問い合わせ先など）")}
          </h2>
          <div className={panelCss}>
            <button
              type="button"
              className={linkRowCss}
              onClick={openMiniAppDetail}
            >
              <span className={rowLabelCss}>{t("ミニアプリ詳細へ")}</span>
              <ArrowOutward className={rowIconCss} />
            </button>
          </div>
        </section>

        {tenant.feature.charge && hasCreditCards && (
          <section className={sectionCss}>
            <h2 className={sectionLabelCss}>{t("クレジットカードの管理")}</h2>
            <div className={panelCss}>
              <button
                type="button"
                className={linkRowCss}
                onClick={() =>
                  navigate({
                    to: "/charge/credit-card/manage",
                    search: { from: "settings" },
                  })
                }
              >
                <span className={rowLabelCss}>{t("登録クレジットカード")}</span>
                <ChevronRight className={rowIconCss} />
              </button>
            </div>
          </section>
        )}
      </main>
    </>
  );
}
